import { useEffect, useState } from "react";
import { api, fmtDate, fmtDateTime, fmtNumber } from "@/lib/api";
import { PageHeader } from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sprout, Plus, Wheat, Droplets, BookOpen, Beaker, FlaskConical } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
  planned: "bg-stone-200 text-stone-800",
  planted: "bg-blue-100 text-blue-900",
  growing: "bg-emerald-100 text-emerald-900",
  harvested: "bg-amber-100 text-amber-900",
  closed: "bg-stone-200 text-stone-700",
};

const LOT_STATUSES = ["planned", "planted", "growing", "harvested", "closed"];

export default function HTX() {
  const [lots, setLots] = useState([]);
  const [harvests, setHarvests] = useState([]);
  const [branches, setBranches] = useState([]);
  const [sops, setSops] = useState([]);
  const [skus, setSkus] = useState([]);
  const [open, setOpen] = useState(false);
  const [hOpen, setHOpen] = useState(false);
  const [mOpen, setMOpen] = useState(false);
  const [activeLot, setActiveLot] = useState(null);
  const [activities, setActivities] = useState([]);
  const [actOpen, setActOpen] = useState(false);
  const [form, setForm] = useState({ code: "", crop_name: "", variety: "", area: 0, planted_date: "", expected_harvest_date: "" });
  const [hForm, setHForm] = useState({ sku_id: "", qty: 0, quality_grade: "A", stock_in: true });
  const [mForm, setMForm] = useState({ activity_type: "fertilize", material_type: "fertilizer", material_name: "", material_qty: 0, material_unit: "kg", used_date: "" });
  const [lotStatus, setLotStatus] = useState("planned");

  const load = () => {
    api.get("/htx/lots").then((r) => setLots(r.data));
    api.get("/htx/harvests").then((r) => setHarvests(r.data));
  };
  useEffect(() => {
    load();
    api.get("/branches").then((r) => setBranches(r.data));
    api.get("/sops").then((r) => setSops(r.data));
    api.get("/master/skus").then((r) => setSkus(r.data));
  }, []);

  const openLotDetail = async (lot) => {
    setActiveLot(lot);
    setLotStatus(lot.status || "planned");
    const r = await api.get(`/htx/lots/${lot.id}/activities`);
    setActivities(r.data); setActOpen(true);
  };

  const createLot = async () => {
    try {
      await api.post("/htx/lots", { ...form, area: Number(form.area) });
      toast.success("Đã tạo lô trồng"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Lỗi"); }
  };

  const logActivity = async (type) => {
    const note = prompt(`Ghi chú cho hoạt động "${type}":`);
    if (note === null) return;
    await api.post(`/htx/lots/${activeLot.id}/activities`, { activity_type: type, note });
    const r = await api.get(`/htx/lots/${activeLot.id}/activities`);
    setActivities(r.data); toast.success("Đã ghi nhận");
  };

  const saveMaterialUsage = async () => {
    try {
      await api.post(`/htx/lots/${activeLot.id}/activities`, {
        ...mForm,
        material_qty: Number(mForm.material_qty),
      });
      const r = await api.get(`/htx/lots/${activeLot.id}/activities`);
      setActivities(r.data); setMOpen(false); toast.success("Đã lưu vật tư");
    } catch (e) { toast.error(e?.response?.data?.detail || "Lỗi"); }
  };

  const doHarvest = async () => {
    try {
      await api.post(`/htx/lots/${activeLot.id}/harvest`, { ...hForm, qty: Number(hForm.qty) });
      toast.success("Thu hoạch đã nhập kho"); setHOpen(false); setActOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Lỗi"); }
  };

  const saveLotStatus = async () => {
    try {
      await api.post(`/htx/lots/${activeLot.id}/status`, { status: lotStatus });
      toast.success("Đã cập nhật trạng thái"); load();
      setActiveLot((prev) => prev ? { ...prev, status: lotStatus } : prev);
    } catch (e) { toast.error(e?.response?.data?.detail || "Lỗi"); }
  };

  return (
    <div data-testid="page-htx">
      <PageHeader
        kicker="P4 • Nông nghiệp"
        title="HTX & Nông sản"
        breadcrumb={["Nông nghiệp", "HTX"]}
        actions={
          <Button onClick={() => setOpen(true)} className="bg-emerald-800 hover:bg-emerald-900 rounded-sm" data-testid="btn-create-lot">
            <Plus className="w-4 h-4 mr-1.5" />Lô trồng mới
          </Button>
        }
      />
      <div className="p-6 lg:p-8">
        <Tabs defaultValue="lots">
          <TabsList className="bg-stone-100 rounded-sm">
            <TabsTrigger value="lots" className="rounded-sm data-[state=active]:bg-white" data-testid="tab-lots">Lô trồng ({lots.length})</TabsTrigger>
            <TabsTrigger value="harvests" className="rounded-sm data-[state=active]:bg-white" data-testid="tab-harvests">Thu hoạch ({harvests.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="lots" className="mt-3">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {lots.map((l) => (
                <button key={l.id} onClick={() => openLotDetail(l)} className="text-left bg-white border border-stone-200 rounded-sm p-4 hover:border-emerald-700 hover:shadow-sm transition" data-testid={`lot-${l.code}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">{l.code}</div>
                      <h3 className="font-serif text-lg font-bold flex items-center gap-1.5"><Sprout className="w-4 h-4 text-emerald-700" />{l.crop_name}</h3>
                      <div className="text-xs text-stone-500 mt-0.5">{l.variety && `Giống ${l.variety} • `}{l.branch_name || "—"}</div>
                    </div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold ${STATUS_COLOR[l.status] || "bg-stone-100"}`}>{l.status}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-2 text-center pt-2 border-t border-stone-100">
                    <div><div className="text-[10px] uppercase text-stone-500 font-bold">Diện tích</div><div className="font-serif font-bold">{fmtNumber(l.area)} m²</div></div>
                    <div><div className="text-[10px] uppercase text-stone-500 font-bold">Trồng</div><div className="text-xs font-mono">{fmtDate(l.planted_date)}</div></div>
                    <div><div className="text-[10px] uppercase text-stone-500 font-bold">Thu hoạch</div><div className="text-xs font-mono">{fmtDate(l.expected_harvest_date)}</div></div>
                  </div>
                  {l.sop_name && <div className="text-xs text-emerald-800 mt-2 flex items-center gap-1"><BookOpen className="w-3 h-3" />{l.sop_name}</div>}
                </button>
              ))}
              {lots.length === 0 && <div className="col-span-full text-center text-stone-400 py-10">Chưa có lô trồng</div>}
            </div>
          </TabsContent>

          <TabsContent value="harvests" className="mt-3">
            <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>{["Thời gian","Lô","Cây trồng","SKU","Sản lượng","Phẩm cấp","Nhập kho"].map(h => <th key={h} className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-stone-600 font-bold">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {harvests.map((h) => (
                    <tr key={h.id}>
                      <td className="px-4 py-2 text-xs font-mono">{fmtDateTime(h.harvested_at)}</td>
                      <td className="px-4 py-2 font-mono text-xs">{h.lot_code}</td>
                      <td className="px-4 py-2 font-semibold">{h.crop_name}</td>
                      <td className="px-4 py-2 text-xs">{h.sku_name || "—"}</td>
                      <td className="px-4 py-2 font-mono font-bold flex items-center gap-1"><Wheat className="w-3 h-3 text-amber-600" />{fmtNumber(h.qty)}</td>
                      <td className="px-4 py-2"><span className="text-xs px-1.5 py-0.5 bg-stone-100 rounded-sm font-bold">{h.quality_grade}</span></td>
                      <td className="px-4 py-2">{h.stocked_in ? <span className="text-emerald-700 text-xs">✓ đã nhập</span> : <span className="text-stone-400 text-xs">chưa</span>}</td>
                    </tr>
                  ))}
                  {harvests.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-stone-400">Chưa có thu hoạch</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Lot creation */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Lô trồng mới</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Mã lô *</Label><Input value={form.code} onChange={(e) => setForm({...form, code: e.target.value})} placeholder="LO-001" className="h-9 rounded-sm mt-1" data-testid="f-lot-code" /></div>
            <div><Label className="text-xs">Cây trồng *</Label><Input value={form.crop_name} onChange={(e) => setForm({...form, crop_name: e.target.value})} placeholder="Rau xà lách" className="h-9 rounded-sm mt-1" /></div>
            <div><Label className="text-xs">Giống</Label><Input value={form.variety} onChange={(e) => setForm({...form, variety: e.target.value})} className="h-9 rounded-sm mt-1" /></div>
            <div><Label className="text-xs">Diện tích (m²)</Label><Input type="number" value={form.area} onChange={(e) => setForm({...form, area: e.target.value})} className="h-9 rounded-sm mt-1" /></div>
            <div>
              <Label className="text-xs">Chi nhánh / HTX</Label>
              <Select value={form.branch_id} onValueChange={(v) => setForm({...form, branch_id: v})}>
                <SelectTrigger className="h-9 rounded-sm mt-1"><SelectValue placeholder="Chọn" /></SelectTrigger>
                <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">SOP kỹ thuật</Label>
              <Select value={form.technique_sop_id} onValueChange={(v) => setForm({...form, technique_sop_id: v})}>
                <SelectTrigger className="h-9 rounded-sm mt-1"><SelectValue placeholder="Không bắt buộc" /></SelectTrigger>
                <SelectContent>{sops.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Ngày trồng</Label><Input type="date" value={form.planted_date} onChange={(e) => setForm({...form, planted_date: e.target.value})} className="h-9 rounded-sm mt-1" /></div>
            <div><Label className="text-xs">Dự kiến thu hoạch</Label><Input type="date" value={form.expected_harvest_date} onChange={(e) => setForm({...form, expected_harvest_date: e.target.value})} className="h-9 rounded-sm mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Hủy</Button>
            <Button onClick={createLot} className="bg-emerald-800 hover:bg-emerald-900 rounded-sm" data-testid="btn-save-lot">Tạo lô</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lot detail + activity log */}
      <Dialog open={actOpen} onOpenChange={setActOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2"><Sprout className="w-5 h-5 text-emerald-700" />{activeLot?.crop_name}</DialogTitle>
          </DialogHeader>
          {activeLot && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-stone-50 p-2 rounded-sm"><div className="text-[10px] uppercase text-stone-500 font-bold">Mã</div><div className="font-mono">{activeLot.code}</div></div>
                <div className="bg-stone-50 p-2 rounded-sm"><div className="text-[10px] uppercase text-stone-500 font-bold">Diện tích</div><div className="font-mono">{activeLot.area} m²</div></div>
              </div>
              <div className="bg-stone-50 p-2 rounded-sm flex items-center justify-between gap-2">
                <div>
                  <div className="text-[10px] uppercase text-stone-500 font-bold">Trạng thái</div>
                  <span className={`inline-flex text-[10px] px-1.5 py-0.5 rounded-sm font-semibold mt-1 ${STATUS_COLOR[activeLot.status] || "bg-stone-100"}`}>{activeLot.status}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={lotStatus} onValueChange={setLotStatus}>
                    <SelectTrigger className="h-9 w-36 rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{LOT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button variant="outline" onClick={saveLotStatus} className="rounded-sm">Lưu</Button>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-stone-500 font-bold mb-2">Hành động nhanh</div>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  <Button onClick={() => logActivity("water")} variant="outline" className="rounded-sm flex-col h-14 text-xs"><Droplets className="w-4 h-4 mb-0.5" />Tưới</Button>
                  <Button onClick={() => logActivity("fertilize")} variant="outline" className="rounded-sm flex-col h-14 text-xs"><FlaskConical className="w-4 h-4 mb-0.5" />Bón</Button>
                  <Button onClick={() => logActivity("spray")} variant="outline" className="rounded-sm flex-col h-14 text-xs"><Droplets className="w-4 h-4 mb-0.5" />Phun</Button>
                  <Button onClick={() => logActivity("inspect")} variant="outline" className="rounded-sm flex-col h-14 text-xs"><BookOpen className="w-4 h-4 mb-0.5" />Kiểm</Button>
                  <Button onClick={() => setMOpen(true)} variant="outline" className="rounded-sm flex-col h-14 text-xs"><Beaker className="w-4 h-4 mb-0.5" />Vật tư</Button>
                </div>
                <Button onClick={() => setHOpen(true)} className="w-full mt-3 h-11 bg-amber-700 hover:bg-amber-800 rounded-sm" data-testid="btn-harvest"><Wheat className="w-4 h-4 mr-1.5" />Thu hoạch & nhập kho</Button>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-stone-500 font-bold mb-2">Nhật ký ({activities.length})</div>
                <div className="max-h-60 overflow-y-auto space-y-1">
                  {activities.map((a) => (
                    <div key={a.id} className="text-xs border-l-2 border-emerald-700 pl-2 py-1">
                      <div className="font-semibold capitalize">{a.activity_type}</div>
                      <div className="text-stone-600">{a.note}</div>
                      {a.material_name && (
                        <div className="text-stone-700">
                          {a.material_type} • {a.material_name} • {fmtNumber(a.material_qty)} {a.material_unit}
                          {a.used_date ? ` • ${fmtDate(a.used_date)}` : ""}
                        </div>
                      )}
                      <div className="text-stone-400 font-mono text-[10px]">{fmtDateTime(a.performed_at)} • {a.performer || "—"}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Harvest */}
      <Dialog open={hOpen} onOpenChange={setHOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Thu hoạch lô {activeLot?.code}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">SKU nông sản *</Label>
              <Select value={hForm.sku_id} onValueChange={(v) => setHForm({...hForm, sku_id: v})}>
                <SelectTrigger className="h-9 rounded-sm mt-1"><SelectValue placeholder="Chọn SKU" /></SelectTrigger>
                <SelectContent>{skus.filter(s => s.category?.toLowerCase().includes("nông") || true).map((s) => <SelectItem key={s.id} value={s.id}>{s.code} - {s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Sản lượng *</Label><Input type="number" value={hForm.qty} onChange={(e) => setHForm({...hForm, qty: e.target.value})} className="h-9 rounded-sm mt-1" data-testid="f-harvest-qty" /></div>
              <div>
                <Label className="text-xs">Phẩm cấp</Label>
                <Select value={hForm.quality_grade} onValueChange={(v) => setHForm({...hForm, quality_grade: v})}>
                  <SelectTrigger className="h-9 rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="A">A</SelectItem><SelectItem value="B">B</SelectItem><SelectItem value="C">C</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={hForm.stock_in} onChange={(e) => setHForm({...hForm, stock_in: e.target.checked})} />
              Tự động nhập vào kho tổng
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHOpen(false)} className="rounded-sm">Hủy</Button>
            <Button onClick={doHarvest} className="bg-amber-700 hover:bg-amber-800 rounded-sm" data-testid="btn-save-harvest">Thu hoạch</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Material usage */}
      <Dialog open={mOpen} onOpenChange={setMOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-serif text-2xl flex items-center gap-2"><Beaker className="w-5 h-5 text-emerald-700" />Ghi nhận vật tư</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Loại hoạt động</Label>
              <Select value={mForm.activity_type} onValueChange={(v) => setMForm({...mForm, activity_type: v})}>
                <SelectTrigger className="h-9 rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fertilize">Bón phân</SelectItem>
                  <SelectItem value="spray">Phun thuốc</SelectItem>
                  <SelectItem value="material_usage">Vật tư khác</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Loại vật tư</Label>
              <Select value={mForm.material_type} onValueChange={(v) => setMForm({...mForm, material_type: v})}>
                <SelectTrigger className="h-9 rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fertilizer">Phân bón</SelectItem>
                  <SelectItem value="pesticide">Thuốc BVTV</SelectItem>
                  <SelectItem value="material">Vật tư</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="text-xs">Tên vật tư *</Label><Input value={mForm.material_name} onChange={(e) => setMForm({...mForm, material_name: e.target.value})} className="h-9 rounded-sm mt-1" /></div>
            <div><Label className="text-xs">Số lượng *</Label><Input type="number" value={mForm.material_qty} onChange={(e) => setMForm({...mForm, material_qty: e.target.value})} className="h-9 rounded-sm mt-1" /></div>
            <div><Label className="text-xs">Đơn vị *</Label><Input value={mForm.material_unit} onChange={(e) => setMForm({...mForm, material_unit: e.target.value})} className="h-9 rounded-sm mt-1" /></div>
            <div className="col-span-2"><Label className="text-xs">Ngày sử dụng</Label><Input type="date" value={mForm.used_date} onChange={(e) => setMForm({...mForm, used_date: e.target.value})} className="h-9 rounded-sm mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMOpen(false)} className="rounded-sm">Hủy</Button>
            <Button onClick={saveMaterialUsage} className="bg-emerald-800 hover:bg-emerald-900 rounded-sm">Lưu vật tư</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
